import { useQuery, useInfiniteQuery } from '@tanstack/react-query';
import { api } from '@/lib/axios';
import { API_ROUTES } from '@/constants/routes';
import type { Lead, DashboardStats } from '@/types/leads';

const KEYS = {
  all: ['places'] as const,
  list: (search: string) => [...KEYS.all, 'list', search] as const,
  stats: () => [...KEYS.all, 'stats'] as const,
};

interface PlacesPage {
  count: number;
  next: string | null;
  previous: string | null;
  results: Lead[];
}

const PAGE_SIZE = 20;

interface UsePlacesOptions {
  search?: string;
}

export const usePlaces = (options?: UsePlacesOptions) => {
  const search = options?.search || '';
  return useInfiniteQuery<PlacesPage>({
    queryKey: KEYS.list(search),
    queryFn: async ({ pageParam = 0 }) => {
      const params: Record<string, string | number> = {
        limit: PAGE_SIZE,
        offset: pageParam as number,
      };
      if (search) params.search = search;
      const { data } = await api.get(API_ROUTES.places, { params });
      if (data.results) {
        return data as PlacesPage;
      }
      return {
        count: data.length,
        next: null,
        previous: null,
        results: data,
      };
    },
    initialPageParam: 0,
    getNextPageParam: (lastPage, allPages) => {
      if (!lastPage.next) return undefined;
      const loaded = allPages.reduce((sum, page) => sum + page.results.length, 0);
      return loaded;
    },
  });
};

/** Flatten all loaded pages into a single Lead array */
export const flattenPlaces = (data: { pages: PlacesPage[] } | undefined): Lead[] => {
  if (!data) return [];
  return data.pages.flatMap((page) => page.results);
};

export const useDashboardStats = () => {
  return useQuery<DashboardStats>({
    queryKey: KEYS.stats(),
    queryFn: async () => {
      const { data } = await api.get(API_ROUTES.placesStats);
      return data;
    },
  });
};
